<script setup lang="ts">
import type { ChallengeGoal } from '~/types/challenge'

const props = defineProps<{
  goal: ChallengeGoal
  fallbackIcon: string
}>()

const state = reactive({
  modalOpen: false
})

const isCompleted = computed(() => {
  return props.goal.progress && props.goal.progress >= props.goal.target
})

const imageClasses = computed(() => {
  if (!isCompleted.value) {
    return 'tw-opacity-25 tw-grayscale'
  }

  return ''
})

const percentage = computed(() => {
  if (!props.goal.progress || !props.goal.target) {
    return 0
  }

  return (props.goal.progress / props.goal.target) * 100
})

const openImageModal = () => {
  if (!props.goal.image_url) {
    return
  }

  state.modalOpen = true
}
</script>

<template>
  <div
    class="tw-relative tw-aspect-square tw-rounded-full tw-bg-white tw-text-center tw-shadow-inner"
    :class="{
      'tw-border': props.goal.target === 1,
      'tw-cursor-pointer': props.goal.image_url
    }"
  >
    <ChartsCakeDiagram
      v-if="props.goal.target > 1 && !isCompleted"
      :percentage="percentage"
      :radius="80"
      :thickness="7"
      path-classes="tw-stroke-riser-blue"
      class="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full"
    />

    <div
      class="tw-relative tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-p-2"
      :class="imageClasses"
    >
      <img
        v-if="props.goal.image_url"
        :src="props.goal.image_url"
        class="tw-w-full tw-object-cover"
        @click="openImageModal"
      />

      <FontAwesomeIcon v-else :icon="props.fallbackIcon" class="tw-text-3xl" />
    </div>

    <div
      v-if="isCompleted"
      class="shimmer tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full tw-rounded-full"
      @click="openImageModal"
    />

    <div
      v-if="isCompleted"
      class="tw-absolute tw-right-[15%] tw-top-[15%] tw-flex tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-translate-x-1/2 tw-items-center tw-justify-center tw-rounded-full tw-bg-riser-green tw-text-xs tw-text-white"
    >
      <FontAwesomeIcon icon="check" />
    </div>

    <Modal
      v-if="props.goal.image_url && state.modalOpen"
      hide-header
      @close="state.modalOpen = false"
    >
      <template #body>
        <div class="tw-sticky tw-top-0 tw-w-full">
          <InputAppButton
            class="tw-ml-auto tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-300 tw-text-white"
            @click="state.modalOpen = false"
          >
            <FontAwesomeIcon icon="xmark" />
          </InputAppButton>
        </div>

        <img
          :src="props.goal.image_url"
          class="tw-mx-auto"
          :class="[imageClasses, { animated: isCompleted }]"
        />
      </template>
    </Modal>
  </div>
</template>

<style scoped>
@keyframes shimmer {
  to {
    background-position-x: 0%;
  }
}

.shimmer:hover {
  background: linear-gradient(-45deg, transparent 40%, #fafafa 50%, transparent 60%);
  background-size: 300%;
  background-position-x: 100%;
  animation: shimmer 0.6s ease-in-out;
}

@keyframes rotateY {
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(360deg);
  }
}

@keyframes scale {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.animated {
  animation-composition: add;

  animation:
    rotateY 1.5s cubic-bezier(0.68, -0.6, 0.32, 1.6),
    scale 0.6s linear;
}
</style>
